import {Signal, TemplateRef, WritableSignal, signal} from '@angular/core';
import {Observable, Subject} from 'rxjs';

import {TLabel} from '@mydeals/i18n';

export class DrawerRef<TResult = unknown, TData = unknown> {
  private readonly _title: WritableSignal<TLabel | undefined>;
  private readonly _footer: WritableSignal<TemplateRef<unknown> | null> = signal(null);
  private readonly _visible: WritableSignal<boolean> = signal(false);
  private readonly _closed$ = new Subject<TResult | undefined>();
  private isClosed = false;
  private closeGuard: (() => Promise<boolean>) | null = null;
  private defaultResult: TResult | undefined;
  private guardPending = false;

  readonly closed$: Observable<TResult | undefined> = this._closed$.asObservable();
  readonly title: Signal<TLabel | undefined>;
  readonly footer: Signal<TemplateRef<unknown> | null> = this._footer.asReadonly();
  /** Drives the drawer enter/leave animation. */
  readonly visible: Signal<boolean> = this._visible.asReadonly();

  constructor(
    readonly data: TData | undefined,
    initialTitle: TLabel | undefined,
  ) {
    this._title = signal(initialTitle);
    this.title = this._title.asReadonly();
  }

  /** Whether close() has already been requested. */
  get closed(): boolean {
    return this.isClosed;
  }

  /** Host: start the enter animation. Never re-shows a drawer that is already closing. */
  show(): void {
    if (this.isClosed) return;
    this._visible.set(true);
  }

  setTitle(title: TLabel): void {
    this._title.set(title);
  }

  setFooter(template: TemplateRef<unknown> | null): void {
    this._footer.set(template);
  }

  /**
   * The result a close without an explicit one resolves with — so a header ×/backdrop close still
   * reports what the body has already done (e.g. "something was saved, reload the list").
   */
  setResult(result: TResult): void {
    this.defaultResult = result;
  }

  /** A check user dismissals (`requestClose`) must pass first, e.g. "discard unsaved changes?". */
  setCloseGuard(guard: (() => Promise<boolean>) | null): void {
    this.closeGuard = guard;
  }

  /**
   * User-initiated dismissal (header ×, backdrop, a body's own cancel button) — runs the close
   * guard first. Resolves `true` once closed. `close()` stays unconditional for programmatic
   * closes that already are the outcome (a successful save, a finished flow).
   */
  async requestClose(result?: TResult): Promise<boolean> {
    if (this.isClosed) return true;
    if (this.guardPending) return false;

    if (this.closeGuard) {
      this.guardPending = true;
      try {
        if (!(await this.closeGuard())) return false;
      } finally {
        this.guardPending = false;
      }
    }

    this.close(result);
    return true;
  }

  close(result?: TResult): void {
    if (this.isClosed) return;
    this.isClosed = true;
    // The guard closes over the body component — drop it so a closed ref (kept by `DrawerService`
    // until the next `open()`) doesn't keep that destroyed component alive.
    this.closeGuard = null;
    this._closed$.next(result ?? this.defaultResult);
    this._closed$.complete();
    // Flip visibility to play the leave animation; the host removes the drawer
    // from the DOM only after p-drawer's (onHide) fires.
    this._visible.set(false);
  }
}
